import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { unwrap, messageOf } from '@truecare/api-client';
import { api } from '../api';
import { TotpEnrollModal } from './TotpEnrollModal';

/**
 * Shared account-security surface — password + optional 2FA — for any signed-in
 * persona (caregiver, facility user, homecare client). Every action is
 * User-level (/auth/*), so nothing here is persona-specific. Extracted from the
 * Phase 17 caregiver Security section when the facility /me adopted
 * <PersonaWorkspace>; the password logic mirrors the admin /me PasswordSection.
 */
export function AccountSecuritySection() {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => unwrap(api.GET('/api/v1/auth/me')),
  });
  const [showTotpModal, setShowTotpModal] = useState(false);

  const me = meQuery.data as
    | { email?: string; hasPassword?: boolean; totpEnrolled?: boolean }
    | undefined;
  // Default hasPassword=true until loaded so we never briefly offer a
  // passwordless "Set a password" to someone who has one.
  const hasPassword = me?.hasPassword ?? true;
  const totpEnrolled = me?.totpEnrolled ?? false;

  return (
    <div className="flex flex-col gap-6">
      {/* Passwordless (magic-link-only) accounts set a FIRST password via the
          reset-email flow, not an in-page form (that was confusing + buggy) —
          they get a one-click "email me a link" card instead. */}
      {meQuery.isSuccess ? (
        hasPassword ? (
          <PasswordCard />
        ) : me?.email ? (
          <SetPasswordByEmailCard email={me.email} />
        ) : null
      ) : null}

      <Card padding="lg" accent="left" tone="forest">
        <CardHeader
          title="Two-factor authentication"
          description="Optional — adds a second step at sign-in using an authenticator app, so your account is safe even if your password leaks."
        />
        <div className="mt-4">
          {totpEnrolled ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Alert tone="success" className="grow">
                Two-factor authentication is enabled.
              </Alert>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowTotpModal(true)}
              >
                Re-enroll
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={() => setShowTotpModal(true)}
              data-testid="account-enable-2fa"
            >
              Enable two-factor authentication
            </Button>
          )}
        </div>
      </Card>

      {showTotpModal && (
        <TotpEnrollModal
          onClose={() => {
            setShowTotpModal(false);
            void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
          }}
        />
      )}
    </div>
  );
}

// Passwordless account: one click emails a set-password link (the standard
// reset flow — single-use, expiring). The link page sets the password, signs
// out other sessions and starts a fresh one; back here auth/me refetches with
// hasPassword=true, so the change-password card shows instead.
function SetPasswordByEmailCard({ email }: { email: string }) {
  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.POST('/api/v1/auth/password-reset/request' as never, {
          body: { email },
        } as never),
      );
    },
  });

  return (
    <Card padding="lg" accent="left" tone="forest">
      <CardHeader
        title="Set a password"
        description="You sign in with emailed links. To also sign in with a password, we'll email you a link to set one."
      />
      <div className="mt-4 flex flex-col gap-4">
        {mutation.isSuccess ? (
          <Alert tone="success">Check {email} for a link to set your password.</Alert>
        ) : (
          <div>
            <Button
              type="button"
              variant="primary"
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Email me a link
            </Button>
          </div>
        )}
        {mutation.isError && (
          <Alert tone="danger">{messageOf(mutation.error)}</Alert>
        )}
      </div>
    </Card>
  );
}

// Change-only: a passwordless account never renders this (the parent gates on
// hasPassword) — it gets SetPasswordByEmailCard instead.
function PasswordCard() {
  const qc = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.POST('/api/v1/auth/password' as never, {
          body: { currentPassword, newPassword },
        } as never),
      );
    },
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDone(true);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  // Backend NewPasswordSchema requires min(12) — mirror so the form doesn't
  // submit a guaranteed 400.
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 12 && passwordsMatch;

  return (
    <Card padding="lg" accent="left" tone="forest">
      <CardHeader
        title="Password"
        description="≥12 characters; current password required to confirm."
      />
      <div className="mt-4 flex flex-col gap-4">
        <Field label="Current password">
          <Input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              setDone(false);
            }}
          />
        </Field>
        <Field label="New password">
          <Input
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setDone(false);
            }}
          />
        </Field>
        <Field label="Confirm new password">
          <Input
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setDone(false);
            }}
          />
        </Field>
        {confirmPassword.length > 0 && !passwordsMatch && (
          <Alert tone="warning">New passwords don't match.</Alert>
        )}
        {mutation.isError && (
          <Alert tone="danger">{messageOf(mutation.error)}</Alert>
        )}
        {done && <Alert tone="success">Password updated.</Alert>}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Update password
          </Button>
        </div>
      </div>
    </Card>
  );
}
